<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 640 : 'auto'"
    style="z-index: 1200 !important;"
  >
    <template v-slot:activator="{ props }">
      <div v-bind="props"><slot /></div>
    </template>

    <v-form ref="form" @submit.prevent="submit">
      <v-card>
        <v-card-title class="d-flex justify-space-between">
          {{ integration ? $t('routes.webhookIntegrations.editIntegration') : $t('routes.webhookIntegrations.createIntegration') }}
          <v-btn icon variant="text" @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pb-0 pt-4 scrollable">
          <v-text-field
            v-model="name"
            :label="$t('routes.webhookIntegrations.integrationName')"
            :rules="nameRules"
            variant="solo-filled"
            flat
            required
          />
          <v-textarea
            v-model="description"
            :label="$t('routes.webhookIntegrations.description')"
            variant="solo-filled"
            flat
            rows="2"
          />
          <v-select
            v-model="type"
            :items="integrationTypes"
            :label="$t('routes.webhookIntegrations.integrationType')"
            :rules="typeRules"
            item-title="label"
            item-value="id"
            variant="solo-filled"
            flat
            required
          />
          <v-select
            v-model="state"
            :items="integrationStates"
            :label="$t('routes.webhookIntegrations.state')"
            variant="solo-filled"
            flat
          />

          <v-alert v-if="errorMessage" type="error" variant="tonal" class="mb-4">
            {{ errorMessage }}
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn type="submit" class="bg-primary" :disabled="isSubmitting">
            {{ $t('routes.webhookIntegrations.save') }}
          </v-btn>
          <v-btn variant="text" :disabled="isSubmitting" @click="dialog = false">
            {{ $t('common.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import { useWebhookIntegrationStore } from '@/stores/WebhookIntegrationStore.js';
import { getWebhookApiErrorMessage } from '@/routes/console/webhook-integrations/WebhookApiError.js';
import { INTEGRATION_STATES } from '../../WebhookConstants.js';

export default {
  name: 'WebhookIntegrationDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    integration: {
      type: Object,
      default: null,
    },
    nodeId: {
      type: String,
      required: true,
    },
    integrationTypes: {
      type: Array,
      required: true,
    },
  },
  emits: ['update:modelValue', 'saved'],
  data() {
    return {
      activatorDialog: false,
      name: '',
      description: '',
      type: null,
      state: 'active',
      errorMessage: '',
      isSubmitting: false,
      nameRules: [
        v => !!v || this.$t('routes.webhookIntegrations.nameRequired'),
        v => (v && v.length <= 255) || this.$t('routes.webhookIntegrations.nameTooLong'),
      ],
      typeRules: [
        v => !!v || this.$t('routes.webhookIntegrations.typeRequired'),
      ],
    };
  },
  watch: {
    dialog: {
      immediate: true,
      handler(value) {
        if (value) this.initializeForm();
      },
    },
    integration() {
      if (this.dialog) this.initializeForm();
    },
  },
  computed: {
    dialog: {
      get() {
        return this.integration ? this.modelValue : this.activatorDialog;
      },
      set(value) {
        if (this.integration) {
          this.$emit('update:modelValue', value);
          return;
        }
        this.activatorDialog = value;
      },
    },
    integrationStates() {
      return INTEGRATION_STATES;
    },
  },
  methods: {
    initializeForm() {
      if (this.$refs.form) this.$refs.form.resetValidation();
      this.name = this.integration?.name || '';
      this.description = this.integration?.description || '';
      this.type = this.integration?.type || this.integrationTypes[0]?.id || null;
      this.state = this.integration?.state || 'active';
      this.errorMessage = '';
      this.isSubmitting = false;
    },
    async submit() {
      const { valid } = await this.$refs.form.validate();
      if (!valid || this.isSubmitting) return;

      this.isSubmitting = true;
      try {
        const store = useWebhookIntegrationStore();
        const payload = {
          nodeId: this.nodeId,
          integrationId: this.integration?.id,
          name: this.name,
          description: this.description,
          type: this.type,
          state: this.state,
        };
        const result = this.integration
          ? await store.updateIntegration(payload)
          : await store.createIntegration(payload);
        this.dialog = false;
        this.$emit('saved', result);
      } catch (error) {
        this.errorMessage = getWebhookApiErrorMessage(error, this.$t('routes.webhookIntegrations.integrationSaveError'));
      } finally {
        this.isSubmitting = false;
      }
    },
  },
}
</script>
